import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Mail, Globe, MapPin, Clock, Phone, MessageCircle,
  Stethoscope, Heart, Activity, Building, UserPlus,
  Instagram, Linkedin,
  ShieldCheck, Lock, HeartHandshake, BadgeCheck, Send,
} from 'lucide-react';
import SEO from '../components/SEO';

const BRAND = '#3B1C73';
const BRAND_HOVER = '#2c1457';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '919955047819'; // Physign business WhatsApp

const QUICK_ACTIONS = [
  { label: 'Book a Doctor', icon: Stethoscope, link: '/search?role=doctor' },
  { label: 'Book a Nurse', icon: Heart, link: '/search?role=nurse' },
  { label: 'Book a Physiotherapist', icon: Activity, link: '/search?role=doctor&spec=physiotherapist' },
  { label: 'Become a Healthcare Provider', icon: UserPlus, link: '/provider-signup' },
  { label: 'Partner with Physign', icon: Building, link: '#provider-section' },
];

const PROVIDER_LINKS = [
  { label: 'Doctor Registration', desc: 'Join as a verified doctor', link: '/provider-signup?role=doctor', icon: Stethoscope },
  { label: 'Nurse Registration', desc: 'Provide home care services', link: '/provider-signup?role=nurse', icon: Heart },
  { label: 'Clinic Partnership', desc: 'List your clinic on Physign', link: '/provider-signup?role=clinic', icon: Building },
  { label: 'Physiotherapist Registration', desc: 'Reach more patients', link: '/provider-signup?role=doctor', icon: Activity },
];

const TRUST_ITEMS = [
  { icon: BadgeCheck, title: 'Verified Healthcare Professionals', desc: 'Every doctor and nurse goes through verification before joining the platform.' },
  { icon: Lock, title: 'Secure Booking Process', desc: 'All transactions and personal information are encrypted and protected.' },
  { icon: HeartHandshake, title: 'Patient Support Team', desc: 'Our dedicated team is available 7 days a week to assist you.' },
  { icon: ShieldCheck, title: 'Privacy & Data Protection', desc: 'We never share your medical information with third parties.' },
];

const SOCIAL_LINKS = [
  { icon: Instagram, label: 'Instagram', url: 'https://www.instagram.com/withphysign?igsh=OW9mZHYwN25sbjNk&utm_source=qr' },
  { icon: Linkedin, label: 'LinkedIn', url: 'https://www.linkedin.com/company/physign/' },
];

const MINI_FAQS = [
  { q: 'How do I book a doctor?', a: 'Search by location or specialization, pick a doctor, select a time slot, and confirm. You will receive a WhatsApp confirmation instantly.' },
  { q: 'How can I cancel an appointment?', a: 'Open your dashboard, go to "My Appointments", and tap "Cancel". Cancellation policies apply based on timing.' },
  { q: 'How can doctors join Physign?', a: 'Click "Become a Provider" or visit /provider-signup. Submit your details and credentials — our team reviews within 24-48 hours.' },
];

const Contact = () => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', city: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim() || (!form.email.trim() && !form.phone.trim())) {
      toast.error('Please fill in Name, Message, and at least one of Email or Phone.');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to send message');
      }
      toast.success('Message sent! Our team will get back to you within 24 hours.');
      setForm({ name: '', phone: '', email: '', city: '', subject: '', message: '' });
    } catch (err) {
      toast.error(err.message || 'Could not send. Please try WhatsApp or email.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <SEO
        path="/contact"
        title="Contact Us"
        description="Get in touch with Physign — book a doctor, partner with us, or get healthcare support. Email, WhatsApp, and contact form available."
      />

      {/* Header */}
      <header className="border-b border-slate-100 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-xl font-bold" style={{ color: BRAND, fontFamily: 'Plus Jakarta Sans' }}>Physign</span>
          </Link>
          <nav className="hidden sm:flex items-center gap-6 text-sm">
            <Link to="/" className="text-slate-600 hover:opacity-80">Home</Link>
            <Link to="/about" className="text-slate-600 hover:opacity-80">About</Link>
            <Link to="/faq" className="text-slate-600 hover:opacity-80">FAQ</Link>
            <Link to="/auth" className="text-white px-4 py-2 rounded-full font-medium"
              style={{ background: BRAND }}
              onMouseEnter={(e) => (e.currentTarget.style.background = BRAND_HOVER)}
              onMouseLeave={(e) => (e.currentTarget.style.background = BRAND)}>
              Login / Sign Up
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 py-12 md:py-16 text-center">
        <span className="font-semibold uppercase tracking-wide text-sm" style={{ color: BRAND }}>Contact Us</span>
        <h1 className="text-3xl md:text-5xl font-bold text-slate-900 mt-3">We&apos;d love to hear from you</h1>
        <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
          Whether you have a question about bookings, want to partner with us, or just need a hand — our team is here to help.
        </p>
      </section>

      {/* Contact Info Cards */}
      <section className="max-w-7xl mx-auto px-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Mail, label: 'Support Email', value: 'support@physign.com', href: 'mailto:support@physign.com' },
            { icon: Mail, label: 'Information', value: 'info@physign.com', href: 'mailto:info@physign.com' },
            { icon: MapPin, label: 'Office', value: 'Gurugram, Haryana, India', href: null },
            { icon: Clock, label: 'Working Hours', value: 'Mon – Sun · 8 AM – 10 PM', href: null },
          ].map((c, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${BRAND}1A` }}>
                <c.icon className="w-5 h-5" style={{ color: BRAND }} />
              </div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">{c.label}</p>
              {c.href ? (
                <a href={c.href} className="font-semibold text-slate-900 hover:underline mt-1 block break-all" data-testid={`contact-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
                  {c.value}
                </a>
              ) : (
                <p className="font-semibold text-slate-900 mt-1">{c.value}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Contact Form + WhatsApp */}
      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-10">
        {/* Form */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Send us a message</h2>
          <p className="text-slate-500 mb-6">Fill in the form below and our team will reply within 24 hours.</p>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input type="text" required placeholder="Full Name *" className="px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-purple-500"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
              <input type="tel" placeholder="Phone Number" className="px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-purple-500"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input type="email" placeholder="Email Address" className="px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-purple-500"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
              <input type="text" placeholder="City" className="px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-purple-500"
                value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="contact-city" />
            </div>
            <input type="text" placeholder="Subject" className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-purple-500"
              value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
            <textarea required placeholder="Your Message *" rows={5} className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-purple-500 resize-none"
              value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
            <button type="submit" disabled={sending}
              className="w-full sm:w-auto flex items-center justify-center gap-2 text-white px-6 py-3 rounded-full font-semibold transition-colors disabled:opacity-60"
              style={{ background: BRAND }}
              onMouseEnter={(e) => !sending && (e.currentTarget.style.background = BRAND_HOVER)}
              onMouseLeave={(e) => !sending && (e.currentTarget.style.background = BRAND)}
              data-testid="contact-submit">
              <Send className="w-4 h-4" />
              {sending ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>

        {/* WhatsApp + Quick Actions */}
        <div className="space-y-6">
          {/* WhatsApp card */}
          <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi Physign team, I have a question')}`}
             target="_blank" rel="noreferrer"
             className="block rounded-2xl p-6 text-white" style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
             data-testid="whatsapp-cta">
            <div className="flex items-center gap-3 mb-2">
              <MessageCircle className="w-7 h-7" />
              <h3 className="text-xl font-bold">Chat with our Healthcare Team</h3>
            </div>
            <p className="opacity-90 text-sm">Get instant replies on WhatsApp — bookings, queries, support, partnerships.</p>
            <span className="inline-block bg-white/20 px-4 py-2 rounded-full text-sm font-medium mt-4">Start WhatsApp Chat →</span>
          </a>

          {/* Quick Actions */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">Quick Actions</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {QUICK_ACTIONS.map((qa, i) => (
                <Link key={i} to={qa.link}
                  className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-purple-300 hover:bg-purple-50/40 transition-all"
                  data-testid={`quick-action-${i}`}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${BRAND}1A` }}>
                    <qa.icon className="w-4 h-4" style={{ color: BRAND }} />
                  </div>
                  <span className="text-sm font-medium text-slate-700">{qa.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* For Healthcare Providers */}
      <section id="provider-section" className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="font-semibold uppercase tracking-wide text-sm" style={{ color: BRAND }}>For Healthcare Providers</span>
            <h2 className="text-3xl font-bold text-slate-900 mt-2">Join Physign as a Provider</h2>
            <p className="text-slate-600 mt-3 max-w-xl mx-auto">Reach more patients, manage bookings online, and grow your practice with us.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PROVIDER_LINKS.map((p, i) => (
              <Link key={i} to={p.link} className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-purple-300 hover:shadow-md transition-all"
                data-testid={`provider-link-${i}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: `${BRAND}1A` }}>
                  <p.icon className="w-6 h-6" style={{ color: BRAND }} />
                </div>
                <h3 className="font-semibold text-slate-900">{p.label}</h3>
                <p className="text-xs text-slate-500 mt-1">{p.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Elements */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-slate-900">Why patients trust Physign</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TRUST_ITEMS.map((t, i) => (
            <div key={i} className="text-center p-5">
              <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{ background: `${BRAND}14` }}>
                <t.icon className="w-7 h-7" style={{ color: BRAND }} />
              </div>
              <h3 className="font-semibold text-slate-900">{t.title}</h3>
              <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mini FAQs */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-900">Frequently Asked</h2>
          </div>
          <div className="space-y-3">
            {MINI_FAQS.map((f, i) => (
              <details key={i} className="rounded-xl bg-white border border-slate-200 p-5 cursor-pointer group" data-testid={`contact-faq-${i}`}>
                <summary className="font-semibold text-slate-900 list-none flex justify-between items-center">
                  {f.q}
                  <span className="text-slate-400 group-open:rotate-180 transition-transform" style={{ color: BRAND }}>▼</span>
                </summary>
                <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/faq" className="font-medium hover:underline" style={{ color: BRAND }}>View All FAQs →</Link>
          </div>
        </div>
      </section>

      {/* Social Media */}
      <section className="max-w-7xl mx-auto px-6 py-12 text-center">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Follow us</h3>
        <div className="flex justify-center gap-3">
          {SOCIAL_LINKS.map((s, i) => (
            <a key={i} href={s.url} target="_blank" rel="noreferrer" aria-label={s.label}
              className="w-11 h-11 rounded-full border border-slate-200 flex items-center justify-center hover:scale-110 transition-transform"
              data-testid={`social-${s.label.toLowerCase()}`}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = BRAND)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#e2e8f0')}>
              <s.icon className="w-5 h-5 text-slate-600" />
            </a>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-6">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <p>© 2026 Physign Technologies Pvt. Ltd. All rights reserved.</p>
          <div className="flex gap-5">
            <Link to="/about" className="hover:opacity-80">About</Link>
            <Link to="/faq" className="hover:opacity-80">FAQ</Link>
            <Link to="/terms" className="hover:opacity-80">Terms</Link>
            <Link to="/privacy-policy" className="hover:opacity-80">Privacy</Link>
            <Link to="/contact" className="hover:opacity-80">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
